import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import * as actions from '@actions';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { SessionForm } from '@components/Clinicians/session-form';
import DOMPurify from 'dompurify';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { addSpaces } from '@helpers/htmlUtils';
import { errorModal } from 'vl-common/src/components/Modals';
import { useTranslation } from 'react-i18next';
import { Form } from 'ALIASED-antd';
import { useAppointmentLetterReview } from '@components/Clinicians/Session/hooks/useAppointmentLetterReview';
import useAsyncAwareDebounceCallback from 'vl-common/src/hooks/useAsyncAwareDebounceCallback';
import { useCallback, useEffect } from 'react';

type NoteChangeArgs = {
  field: keyof SessionForm;
  value: string;
  skipSanitisation?: boolean;
};

const allowedTags = [
  'a',
  'b',
  'br',
  'div',
  'h1',
  'h2',
  'h3',
  'i',
  'li',
  'ol',
  'p',
  'span',
  'strong',
  'textarea',
  'u',
  'ul'
];

const decodeHtmlEntities = (html: string): string => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

const sanitiseHTMLText = (noteText: string): string => {
  const sanitisedHTML = DOMPurify.sanitize(noteText, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: [], // Disallow all attributes
    FORCE_BODY: true
  });

  return decodeHtmlEntities(sanitisedHTML);
};

const useSessionNoteChangeHandler = ({ field }: { field: keyof SessionForm }) => {
  const { t } = useTranslation('common');
  const {
    appointment: { appointment_id, audit_appt_id, read_only: readOnly, clinician_user_guid }
  } = useSessionAppointment();

  const dispatch = useDispatch();
  const [, setIsSavingNotes] = useSessionFormState('isSavingNotes', 0);
  const { isLoading: savedNotesLoading } = useAppointmentDetails(audit_appt_id || appointment_id);
  const [, setField] = useSessionFormState(field);
  const form = Form.useFormInstance<SessionForm>();
  const invalidateLetterReview = useAppointmentLetterReview(form);

  const incrementQueue = useCallback(() => setIsSavingNotes((prev) => (prev ? prev + 1 : 1)), [setIsSavingNotes]);
  const decrementQueue = useCallback(() => setIsSavingNotes((prev) => (prev ? prev - 1 : 0)), [setIsSavingNotes]);

  const { mutateAsync: handleNoteChanged } = useMutation({
    mutationKey: ['handleNoteChanged', appointment_id, clinician_user_guid],
    mutationFn: async ({ field, value, skipSanitisation }: NoteChangeArgs) => {
      if (readOnly || savedNotesLoading) return;
      const { error } = await dispatch(
        actions.clinicianNotesPost({
          user_guid: clinician_user_guid,
          appt_id: appointment_id,
          note_code: field,
          note_text: skipSanitisation ? addSpaces(value) : sanitiseHTMLText(addSpaces(value))
        })
      );

      error && errorModal({
        title: t('Error while updating clinical notes'),
        content: error.message,
        okText: t('Close')
      });
    },
  });

  // Async debounce to wait for potential backoff of requests before making next notes update call
  const { debouncedFunction: handleNoteChangeDebounce, hasUnsavedChanges } = useAsyncAwareDebounceCallback(
    async (payload: NoteChangeArgs) => handleNoteChanged(payload),
    750
  );

  // Sync hook to global Form State queue
  useEffect(() => {
    if (hasUnsavedChanges) {
      incrementQueue(); // Field saving/queueing

      // Handle decrement on clean-up or unmount
      return () => {
        decrementQueue();
      }
    }
  }, [hasUnsavedChanges, incrementQueue, decrementQueue]);

  const handleNoteChange = ({ field, value, skipSanitisation }: NoteChangeArgs) => {
    setField(value);
    handleNoteChangeDebounce({ field, value, skipSanitisation });
    invalidateLetterReview({ [field]: value });
  };

  return handleNoteChange;
};

export default useSessionNoteChangeHandler;
